import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Gift, ChevronRight, Sparkles, PartyPopper, Star, X } from 'lucide-react';
import { PrizeWinner } from '@/contexts/PrizeContext';
import confetti from 'canvas-confetti';

interface PrizeWinModalProps {
  winner: PrizeWinner;
  onClose: () => void;
}

export function PrizeWinModal({ winner, onClose }: PrizeWinModalProps) {
  const navigate = useNavigate();
  const [showAnimation, setShowAnimation] = useState(false);

  useEffect(() => {
    setTimeout(() => setShowAnimation(true), 50);
    
    const duration = 4 * 1000;
    const end = Date.now() + duration;

    const colors = ['#fbbf24', '#f59e0b', '#d97706', '#ffffff', '#fef08a'];

    (function frame() {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 70,
        origin: { x: 0, y: 0.7 },
        colors,
        zIndex: 10001,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 70,
        origin: { x: 1, y: 0.7 },
        colors,
        zIndex: 10001,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    })();
  }, []);

  // Claim now - navigates to claim page (only way to dismiss, modal shows until claim is submitted)
  const handleClaimNow = () => {
    navigate(`/prizes/claim/${winner.id}`);
    onClose();
  };

  const formatValue = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop with golden glow - no click handler, forces user to claim */}
      <div
        className={`absolute inset-0 bg-black/95 transition-opacity duration-500 ${showAnimation ? 'opacity-100' : 'opacity-0'}`}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-yellow-500/10 via-transparent to-transparent"></div>
      </div>

      {/* Modal - Centered with max height for mobile safety */}
      <div 
        className={`relative w-full max-w-md max-h-[90dvh] overflow-y-auto rounded-3xl transition-all duration-500 ease-out ${
          showAnimation ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 scale-95 opacity-0'
        }`}
      >
        {/* Glowing border effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-yellow-500/30 via-amber-500/20 to-yellow-500/30 rounded-3xl"></div>
        
        <div className="relative m-[1px] bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950 rounded-3xl overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white/60 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>

          {/* Celebration Header */}
          <div className="relative pt-6 sm:pt-8 pb-2 sm:pb-4 text-center overflow-hidden">
            {/* Animated background glow */}
            <div className="absolute inset-0 bg-gradient-to-b from-yellow-500/20 via-transparent to-transparent animate-pulse"></div>
            
            {/* Stars decoration */}
            <div className="absolute top-4 left-8 animate-bounce" style={{ animationDelay: '0.1s' }}>
              <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
            </div>
            <div className="absolute top-8 right-10 animate-bounce" style={{ animationDelay: '0.3s' }}>
              <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
            </div>
            <div className="absolute top-6 left-1/4 animate-bounce" style={{ animationDelay: '0.5s' }}>
              <Sparkles className="h-5 w-5 text-yellow-300" />
            </div>
            
            <div className="relative flex items-center justify-center gap-2 mb-3">
              <PartyPopper className="h-10 w-10 sm:h-12 sm:w-12 text-yellow-400 animate-bounce" style={{ animationDelay: '0.1s' }} />
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-400/30 blur-xl rounded-full animate-pulse"></div>
                <Trophy className="relative h-14 w-14 sm:h-16 sm:w-16 text-yellow-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.5)]" />
              </div>
              <PartyPopper className="h-10 w-10 sm:h-12 sm:w-12 text-yellow-400 scale-x-[-1] animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
            <h1 className="relative text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-400 drop-shadow-lg">
              YOU WON!
            </h1>
            <p className="relative text-yellow-400/80 font-medium mt-1 text-sm sm:text-base">Congratulations!</p>
          </div>

          {/* Prize Display */}
          <div className="px-4 sm:px-6 pb-6">
            <div className="bg-black/40 border border-yellow-500/20 rounded-2xl overflow-hidden mb-5 shadow-xl">
              {/* Prize Image - 4:3 aspect ratio */}
              <div className="relative aspect-[4/3] bg-gradient-to-br from-yellow-500/10 to-amber-500/10 overflow-hidden">
                {winner.prize_images?.[0] ? (
                  <img
                    src={winner.prize_images[0]}
                    alt={winner.prize_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Gift className="h-14 w-14 sm:h-16 sm:w-16 text-yellow-400/40" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              </div>

              {/* Prize Info */}
              <div className="p-4 text-center">
                <h2 className="text-lg sm:text-xl font-bold text-white mb-1 truncate">{winner.prize_name}</h2>
                <div className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-400">
                  {formatValue(winner.prize_value)}
                </div>
              </div>
            </div>

            {/* CTA Button - Only option is to claim */}
            <button
              onClick={handleClaimNow}
              className="w-full min-h-[56px] bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-lg shadow-yellow-500/30"
            >
              <span className="text-base sm:text-lg">Claim Your Prize</span>
              <ChevronRight className="h-5 w-5" />
            </button>

            <p className="text-[10px] sm:text-xs text-gray-500 text-center mt-4 pb-2">
              Complete the claim form to receive your prize.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
